/**
 * Route prefetch map — matches route paths to their lazy import functions.
 * On hover/focus, we trigger the dynamic import so the chunk is cached
 * before the user clicks, making navigation feel instant.
 */
const routeImports: Record<string, () => Promise<unknown>> = {
  "/search": () => import("@/pages/PropertySearch"),
  "/market": () => import("@/pages/MarketAnalysis"),
  "/calculators": () => import("@/pages/Calculators"),
  "/brrrr": () => import("@/pages/BRRRRPlanner"),
  "/renovations": () => import("@/pages/RenovationROI"),
  "/journey": () => import("@/pages/HomebuyerJourney"),
  "/programs": () => import("@/pages/Programs"),
  "/documents": () => import("@/pages/DocumentVault"),
  "/advisor": () => import("@/pages/BuyNowAdvisor"),
  "/achievements": () => import("@/pages/Achievements"),
  "/auth": () => import("@/pages/Auth"),
  "/profile": () => import("@/pages/ProfileSettings"),
};

/**
 * Heavy secondary chunks (charts, framer-motion-using widgets) that the
 * page-level dynamic import does NOT pull eagerly. Prefetching them alongside
 * the route module means framer-motion/recharts arrive before navigation
 * completes, eliminating the in-route Suspense flash.
 */
const heavyChunkImports: Record<string, Array<() => Promise<unknown>>> = {
  "/": [
    () => import("@/components/charts/AffordabilityLineChart"),
  ],
  "/market": [
    () => import("@/components/charts/MarketPriceLineChart"),
    () => import("@/components/charts/MarketRadarChart"),
    () => import("@/components/charts/MarketDemographicsBarChart"),
  ],
  "/calculators": [
    () => import("@/components/charts/AmortizationAreaChart"),
  ],
  "/brrrr": [
    () => import("@/components/charts/BRRRRBreakdownChart"),
  ],
  "/renovations": [
    () => import("@/components/charts/RenovationROIChart"),
  ],
  "/property": [
    () => import("@/components/charts/PropertyCostsPieChart"),
    () => import("@/components/charts/PropertyRenovationsBarChart"),
  ],
};

const prefetched = new Set<string>();

/**
 * Prefetch the JS chunk for a route. Safe to call multiple times —
 * each route is only fetched once.
 */
export function prefetchRoute(path: string) {
  // Normalize path (strip query/hash, handle /property/:id)
  const base = path.split("?")[0].split("#")[0];
  const key = base.startsWith("/property/") ? "/property" : base;
  if (prefetched.has(key)) return;

  const loader = routeImports[key];
  const heavy = heavyChunkImports[key];
  if (loader || heavy) {
    prefetched.add(key);
    loader?.();
    heavy?.forEach((fn) => fn());
  }
}

/**
 * Returns onMouseEnter / onFocus / onTouchStart handlers that trigger prefetch.
 * Touch handler covers mobile users who never fire mouseenter.
 */
export function prefetchHandlers(path: string) {
  const handler = () => prefetchRoute(path);
  return { onMouseEnter: handler, onFocus: handler, onTouchStart: handler };
}

/**
 * Prefetch the heaviest routes (and their framer-motion / chart chunks)
 * during browser idle time after first paint. Skips on slow networks or
 * data-saver mode to respect user preferences.
 */
const HEAVY_ROUTES = ["/market", "/calculators", "/brrrr", "/renovations"];

export function prefetchHeavyRoutesOnIdle() {
  if (typeof window === "undefined") return;
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (conn?.saveData) return;
  if (conn?.effectiveType && /(^|-)2g$/.test(conn.effectiveType)) return;

  const run = () => HEAVY_ROUTES.forEach(prefetchRoute);
  const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
  if (ric) ric(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}

import { useEffect, useRef } from "react";

/**
 * Hook: prefetch a route's chunk when the element scrolls into the viewport.
 * Useful for off-screen nav links on long pages (e.g. dashboard tiles).
 * Falls back to immediate prefetch if IntersectionObserver is unavailable.
 */
export function usePrefetchOnView<T extends Element>(path: string) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      prefetchRoute(path);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            prefetchRoute(path);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [path]);
  return ref;
}
